/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    IOLinesMode,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Tibbits from '@appblocks/codegeneration/Tibbits';
import BaseTibbit from '@appblocks/codegeneration/Tibbits/_BaseTibbit';
import IOTibbit from '@appblocks/codegeneration/Tibbits/_IOTibbit';

export const getCodeBlocks = (feature: IOTibbit): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const tibbitName = feature.getSettingValue('NAME');
    const project: ProjectConfiguration = feature.project;
    const ID = feature.getID();
    const currentSocket = Number(feature.getSettingValue('SOCKET'));
    const nextSocket = (currentSocket + 1).toString();

    let connector = '';
    const connectorSocket = nextSocket;
    let connectorTibbit;
    let lcdEnabled;


    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        id: 'io_int_defines',
        template: `
#define INT_EDGE_LOW 1
#define INT_EDGE_HIGH 2
#define INT_EDGE_BOTH 3`,
    });

    for (let i = 0; i < project.features.length; i += 1) {
        if (project.features[i].getSettingValue('SOCKET') === nextSocket) {
            connector = project.features[i].getSettingValue('TIBBIT');
            connectorTibbit = project.features[i];
        }
        if (project.features[i].id === 'lcd') {
            lcdEnabled = project.features[i].getSettingValue('ENABLED');
        }
    }

    // for each line
    for (let i = 1; i <= feature.lines; i += 1) {
        const LNAME = `L${i}NAME`;
        const LMODE = `L${i}MODE`;
        const LSTATE = `L${i}STATE`;
        let LSTATECODE = '';
        let mode = feature.getSettingValue(LMODE);
        if (mode === undefined || mode === '') {
            mode = IOLinesMode.Output;
        }
        const VARNAME = feature.getSettingValue(LNAME);
        let interruptEnum;
        let interruptNumber;
        let interruptValue;
        let interruptBit;

        switch (mode) {
            case IOLinesMode.Button:
                blocks.push({
                    location: 'boot',
                    blockType: BlockType.CODE,
                    template: `
    '${feature.name} with tibbit button input mode on socket ${connectorSocket}
    io.num=S${feature.getSettingValue('SOCKET')}_${feature.getSettingValue(LNAME)}_Button
    io.enabled=NO`,
                });

                blocks.push({
                    location: 'GLOBAL_DECLARATIONS',
                    blockType: BlockType.CODE,
                    template: `
declare ${feature.getSettingValue(LNAME)}_button_state as byte`,
                });

                blocks.push({
                    location: 'GLOBAL_DIMS',
                    blockType: BlockType.CODE,
                    template: `
dim ${feature.getSettingValue(LNAME)}_button_state as byte=HIGH`,
                });

                blocks.push({
                    location: 'GLOBAL_CONSTS',
                    blockType: BlockType.CODE,
                    template: `
const S${feature.getSettingValue('SOCKET')}_${feature.getSettingValue(LNAME)}_Button = ${Tibbits.getSocketPins(feature.getSettingValue('SOCKET'), feature.project.device.id)[i - 1]}`,
                });
                break;
            case IOLinesMode.Standard:
                blocks.push({
                    location: 'boot',
                    blockType: BlockType.CODE,
                    template: `                        
    ${i === 1 ? `
    'Tibbit ${feature.name} setup on socket ${feature.getSettingValue('SOCKET')}` : ''}
    io.num=${feature.getSettingValue(LNAME)}_num
    io.enabled=NO`,
                });
                blocks.push({
                    location: `var_${VARNAME}_set`,
                    blockType: BlockType.CODE,
                    template: `io.lineset(${feature.getSettingValue(LNAME)}_num, value)`,
                });
                break;
            case IOLinesMode.Output:
                {
                    const variable = project.getVariableByName(VARNAME);
                    LSTATECODE = variable?.getSettingValue('DEFAULT') === '1' ? 'io.state=HIGH' : 'io.state=LOW';
                    blocks.push({
                        location: 'boot',
                        blockType: BlockType.CODE,
                        template: `                        
    ${i === 1 ? `
    'Tibbit ${feature.name} setup on socket ${feature.getSettingValue('SOCKET')}` : ''}
    io.num=${feature.getSettingValue(LNAME)}_num
    io.enabled=YES
    ${LSTATECODE}`,
                    });
                    blocks.push({
                        location: `var_${VARNAME}_set`,
                        blockType: BlockType.CODE,
                        template: `io.lineset(${feature.getSettingValue(LNAME)}_num, value)`,
                    });
                }
                break;
            case IOLinesMode.Interrupt:
                interruptNumber = Tibbits.getSocket(feature.getSettingValue('SOCKET'), feature.project.device.id)?.interrupt;
                if (!interruptNumber) {
                    break;
                }
                interruptEnum = PlatformUtils.getEnum(interruptNumber, 'pl_int_num', feature.project.device.platform);
                interruptValue = PlatformUtils.getEnumValue(interruptNumber, 'pl_int_num', feature.project.device.platform);
                interruptBit = Math.pow(2, Number(interruptValue));

                blocks.push({
                    location: 'boot',
                    blockType: BlockType.CODE,
                    id: 'io_int_mode_set',
                    template: 'io.intmode=1',
                });

                blocks.push({
                    location: 'boot',
                    blockType: BlockType.CODE,
                    template: `
    'Tibbit ${feature.name} setup on socket ${feature.getSettingValue('SOCKET')}
    io.num=${feature.getSettingValue(LNAME)}_num
    io.enabled=NO
    io.intnum=S${feature.getSettingValue('SOCKET')}_INT_NUM
    io.intedge=${feature.getSettingValue('INTEDGE')}
    io.intenabled=YES`,
                });

                blocks.push({
                    location: 'on_io_int',
                    blockType: BlockType.CODE,
                    id: feature.getID(),
                    template: `
            if (linestate and ${interruptBit}) <> 0 then
                io.intnum=${interruptNumber}
                dim intcount as word=io.intcount
                io.intcount=0
                '****on_io_int_${interruptNumber}****'
            end if`,
                });

                blocks.push({
                    location: 'GLOBAL_CONSTS',
                    blockType: BlockType.CODE,
                    template: `const S${feature.getSettingValue('SOCKET')}_INT_NUM = ${interruptEnum}`,
                });
                break;
            default:
                break;
        }

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const ${feature.getSettingValue(LNAME)}_num = ${Tibbits.getSocketPins(feature.getSettingValue('SOCKET'), feature.project.device.id)[i - 1]}`,
        });

        blocks.push({
            location: `var_${VARNAME}_update`,
            blockType: BlockType.CODE,
            template: `var_${VARNAME}_update_completed(io.lineget(${feature.getSettingValue(LNAME)}_num))`,
        });
    }

    return blocks;
};
